import {
  customElement,
  type TemplateResult,
  html,
  property,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';

import { demoFunc } from './dees-input-multitoggle.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-multitoggle': DeesInputMultitoggle;
  }
}

@customElement('dees-input-multitoggle')
export class DeesInputMultitoggle extends DeesInputBase<DeesInputMultitoggle> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];


  @property()
  accessor type: 'boolean' | 'multi' | 'single' = 'multi';

  @property()
  accessor booleanTrueName: string = 'true';

  @property()
  accessor booleanFalseName: string = 'false';

  @property({
    type: Array,
  })
  accessor options: string[] = [];

  @property()
  accessor selectedOption: string = '';

  @property({ type: Boolean })
  accessor boolValue: boolean = false;

  // Add value property for form compatibility
  public get value(): string | boolean {
    if (this.type === 'boolean') {
      return this.selectedOption === this.booleanTrueName;
    }
    return this.selectedOption;
  }

  public set value(val: string | boolean) {
    if (this.type === 'boolean' && typeof val === 'boolean') {
      this.selectedOption = val ? this.booleanTrueName : this.booleanFalseName;
    } else {
      this.selectedOption = val as string;
    }
    this.requestUpdate();
    // Defer indicator update to next frame if component not yet updated
    if (this.hasUpdated) {
      requestAnimationFrame(() => {
        this.setIndicator();
      });
    }
  }

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      :host {
        color: var(--dees-color-text-primary);
        user-select: none;
      }

      /* Recessed track (segmented control = tertiary system fill). */
      .selections {
        position: relative;
        display: inline-flex;
        align-items: center;
        height: var(--dees-control-height-lg);
        background: var(--dees-color-fill-tertiary);
        padding: var(--dees-spacing-xs);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);      }

      .option {
        position: relative;
        display: flex;
        align-items: center;
        height: 100%;
        padding: 0 var(--dees-spacing-lg);
        border-radius: var(--dees-radius-sm);
        cursor: pointer;
        white-space: nowrap;
        transition: color var(--dees-transition-default) var(--dees-ease-standard);
        font-size: var(--dees-font-control-size);
        font-weight: var(--dees-font-control-weight);
        color: var(--dees-color-text-secondary);
        line-height: 1;
        z-index: 2;
      }

      /* Hairline separators between unselected segments; they fade out next to
         the thumb. */
      .option::before {
        content: '';
        position: absolute;
        left: 0;
        top: 25%;
        bottom: 25%;
        width: 1px;
        background: var(--dees-color-border-default);
        opacity: 1;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard);
      }

      .option.no-sep::before {
        opacity: 0;
      }

      .option:hover {
        color: var(--dees-color-text-primary);
      }

      .option.selected {
        color: var(--dees-color-text-primary);
        font-weight: 600;
      }

      /* Elevated sliding thumb. White in light, lifted gray in dark (via the
         shared control-thumb tokens) — both carry a tight shadow so the selected
         segment reads as raised off the track. */
      .indicator {
        opacity: 0;
        position: absolute;
        height: calc(100% - (var(--dees-spacing-xs) * 2));
        top: var(--dees-spacing-xs);
        border-radius: var(--dees-radius-sm);
        background: var(--dees-color-control-thumb);
        box-shadow: var(--dees-shadow-control-thumb);
        transition:
          left var(--dees-transition-default) var(--dees-ease-standard),
          width var(--dees-transition-default) var(--dees-ease-standard),
          transform var(--dees-transition-fast) var(--dees-ease-standard);
        z-index: 1;
      }

      .indicator.no-transition {
        transition: none;
      }

      /* Press-and-hold squeeze on the thumb. */
      .selections.pressed .indicator {
        transform: scale(0.95);
      }

      :host([disabled]) .selections {
        opacity: 0.5;
        cursor: not-allowed;
      }

      :host([disabled]) .option {
        cursor: not-allowed;
        pointer-events: none;
      }

      :host([disabled]) .indicator {
        background: var(--dees-color-fill-primary);
        box-shadow: none;
      }
    `,
  ];

  public render(): TemplateResult {
    const selectedIndex = this.options.indexOf(this.selectedOption);
    return html`
      <div class="input-wrapper">
        <dees-label .label=${this.label} .infoText=${this.infoText}></dees-label>
        <div class="mainbox">
          <div
            class="selections"
            @pointerdown=${this.handlePressStart}
            @pointerup=${this.handlePressEnd}
            @pointerleave=${this.handlePressEnd}
            @pointercancel=${this.handlePressEnd}
          >
            <div class="indicator"></div>
            ${this.options.map((option, index) => {
              const isSelected = option === this.selectedOption;
              // A separator sits on an option's leading edge; hide it on the
              // first option and on either side of the thumb.
              const hideSeparator =
                index === 0 || isSelected || index - 1 === selectedIndex;
              return html`<div
                class="option ${isSelected ? 'selected' : ''} ${hideSeparator ? 'no-sep' : ''}"
                @click=${() => this.handleSelection(option)}
              >${option}</div>`;
            })}
          </div>
        </div>
        ${this.renderDescription()}
      </div>
    `;
  }

  private handlePressStart = () => {
    if (this.disabled) return;
    this.shadowRoot?.querySelector('.selections')?.classList.add('pressed');
  };

  private handlePressEnd = () => {
    this.shadowRoot?.querySelector('.selections')?.classList.remove('pressed');
  };

  public async connectedCallback() {
    await super.connectedCallback();
    // Initialize boolean options early
    if (this.type === 'boolean' && this.options.length === 0) {
      this.options = [this.booleanTrueName || 'true', this.booleanFalseName || 'false'];
      // Set default selection for boolean if not set
      if (!this.selectedOption) {
        this.selectedOption = this.booleanFalseName || 'false';
      }
    }
    // Set default selection to first option if not set
    if (!this.selectedOption && this.options.length > 0) {
      this.selectedOption = this.options[0];
    }
  }

  public async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    // Update boolean options if they changed
    if (this.type === 'boolean') {
      this.options = [this.booleanTrueName || 'true', this.booleanFalseName || 'false'];
    }
    // Wait for the next frame to ensure DOM is fully rendered
    await this.updateComplete;
    
    // Wait for fonts to load
    if (document.fonts) {
      await document.fonts.ready;
    }
    
    // Wait one more frame after fonts are loaded
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // Now set the indicator
    this.setIndicator();
  }

  public async handleSelection(optionArg: string) {
    if (this.disabled) return;
    this.selectedOption = optionArg;
    this.requestUpdate();
    this.changeSubject.next(this);
    await this.updateComplete;
    this.setIndicator();
  }
  
  private indicatorInitialized = false;

  public async setIndicator() {
    const indicator: HTMLDivElement | null = this.shadowRoot!.querySelector('.indicator');
    const selectedIndex = this.options.indexOf(this.selectedOption);

    // If no valid selection, hide indicator
    if (selectedIndex === -1 || !indicator) {
      if (indicator) {
        indicator.style.opacity = '0';
      }
      return;
    }

    const option: HTMLDivElement | null = this.shadowRoot!.querySelector(
      `.option:nth-child(${selectedIndex + 2})`
    );
    
    if (indicator && option) {
      // Only disable transition for the very first positioning
      if (!this.indicatorInitialized) {
        indicator.classList.add('no-transition');
        this.indicatorInitialized = true;
        
        // Remove the no-transition class after a brief delay
        setTimeout(() => {
          indicator.classList.remove('no-transition');
        }, 50);
      }
      
      indicator.style.width = `${option.clientWidth}px`;
      indicator.style.left = `${option.offsetLeft}px`;
      indicator.style.opacity = '1';
    }
  }

  public getValue(): string | boolean {
    if (this.type === 'boolean') {
      return this.selectedOption === this.booleanTrueName;
    }
    return this.selectedOption;
  }

  public setValue(value: string | boolean): void {
    if (this.type === 'boolean' && typeof value === 'boolean') {
      this.selectedOption = value ? (this.booleanTrueName || 'true') : (this.booleanFalseName || 'false');
    } else {
      this.selectedOption = value as string;
    }
    this.requestUpdate();
    if (this.hasUpdated) {
      requestAnimationFrame(() => {
        this.setIndicator();
      });
    }
  }
}
